import { fetchUsers as fetchUsersApi } from "@/api";
import PaginationButtons from "@/components/PaginationButtons";
import { SimpleFilter } from "@/components/simple-filter";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTimeFromISO } from "@/lib/formatters";
import { PagyMetadata } from "@/types";
import { Link, router } from "@inertiajs/react";
import { useState } from "react";
import AdminLayout from "./layout";
interface Adjustment {
  public_id: string;
  address: string;
  effective_date: string;
  created_at: string;
  current_page: string;
  status: string;
  user: {
    email: string;
    public_id: string;
  };
}

interface Props {
  adjustments: Adjustment[];
  pagy: PagyMetadata;
  filters: {
    status?: string;
    address?: string;
    user_email?: string;
  };
}

async function fetchUsers() {
  const users = await fetchUsersApi();
  return users.map((user: any) => user.email);
}

type Feedback = { id: string; kind: "success" | "error" } | null;

export default function Component({ adjustments, pagy, filters }: Props) {
  const [users, setUsers] = useState<string[]>([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const handleDownload = async (publicId: string) => {
    const flash = (kind: "success" | "error") => {
      setFeedback({ id: publicId, kind });
      setTimeout(() => setFeedback((f) => (f?.id === publicId ? null : f)), 1500);
    };
    try {
      const res = await fetch(`/admin/adjustments/${publicId}/pull_command`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) {
        flash("error");
        return;
      }
      const { command } = await res.json();
      await navigator.clipboard.writeText(command);
      flash("success");
    } catch {
      flash("error");
    }
  };

  return (
    <AdminLayout pageName="adjustments">
      <div className="overflow-x-auto">
        <div className="text-sm text-muted-foreground pb-4">
          <span className="text-muted-foreground">{pagy.count} rows</span>
        </div>

        <div className="py-4 flex gap-2">
          <SimpleFilter
            label="Status"
            filter={{
              type: "select",
              value: filters.status || null,
              options: ["completed", "not_completed"],
              key: "status",
              onChange: (values) => {
                router.get("/admin/adjustments", {
                  ...filters,
                  status: values,
                });
              },
              onClear: () => {
                const filtersWithoutStatus = {
                  ...filters,
                  status: undefined,
                };
                router.get("/admin/adjustments", filtersWithoutStatus);
              },
            }}
          />

          <SimpleFilter
            label="Address"
            filter={{
              type: "text",
              value: filters.address || null,
              key: "address",
              onChange: (value) => {
                router.get("/admin/adjustments", {
                  ...filters,
                  address: value,
                });
              },
              onClear: () => {
                const filtersWithoutAddress = {
                  ...filters,
                  address: undefined,
                };
                router.get("/admin/adjustments", filtersWithoutAddress);
              },
            }}
          />

          <SimpleFilter
            label="User"
            filter={{
              type: "combobox",
              value: filters.user_email || null,
              key: "user_email",
              onOpen: async () => {
                if (!usersLoaded) {
                  const data = await fetchUsers();
                  setUsers(data);
                  setUsersLoaded(true);
                }
              },
              onChange: (value) => {
                router.get("/admin/adjustments", {
                  ...filters,
                  user_email: value,
                });
              },
              onClear: () => {
                const filtersWithoutUser = {
                  ...filters,
                  user_email: undefined,
                };
                router.get("/admin/adjustments", filtersWithoutUser);
              },
              options: users,
            }}
          />
        </div>

        <div className="rounded-md border overflow-x-auto">
          <Table className="whitespace-nowrap w-full">
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead>Current Page</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>User</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {adjustments.map((adjustment) => (
                <TableRow key={adjustment.public_id}>
                  <TableCell>
                    <HoverCard openDelay={250}>
                      <HoverCardTrigger asChild>
                        <Link className="link" href={`/adjustments/${adjustment.public_id}`}>
                          {adjustment.public_id}
                        </Link>
                      </HoverCardTrigger>
                      <HoverCardContent align="start" className="w-72 p-3">
                        <div className="space-y-2">
                          <div>
                            <div className="text-xs text-muted-foreground">Address</div>
                            <div className="text-sm">{adjustment.address || "—"}</div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <div className="text-xs text-muted-foreground">Status</div>
                              <div className="text-sm">{adjustment.status}</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground">Current page</div>
                              <div className="text-sm">{adjustment.current_page || "—"}</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground">Created</div>
                              <div className="text-sm">
                                {formatDateTimeFromISO(adjustment.created_at)}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground">User</div>
                              <div className="text-sm truncate">{adjustment.user.email}</div>
                            </div>
                          </div>
                          <div className="border-t pt-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownload(adjustment.public_id)}
                              className={
                                feedback?.id === adjustment.public_id && feedback.kind === "error"
                                  ? "text-destructive"
                                  : undefined
                              }
                            >
                              {feedback?.id === adjustment.public_id
                                ? feedback.kind === "success"
                                  ? "Copied"
                                  : "No URL"
                                : "Debug locally"}
                            </Button>
                            {feedback?.id === adjustment.public_id &&
                              feedback.kind === "success" && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Paste the command into your local dev environment
                                </p>
                              )}
                          </div>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  </TableCell>
                  <TableCell>{adjustment.address}</TableCell>
                  <TableCell>{formatDateTimeFromISO(adjustment.created_at)}</TableCell>
                  <TableCell>{adjustment.current_page}</TableCell>
                  <TableCell>{adjustment.status}</TableCell>
                  <TableCell>
                    <Link className="link" href={`/admin/users/${adjustment.user.public_id}`}>
                      {adjustment.user.email}
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="pt-2 flex justify-end">
          <PaginationButtons
            prev={{
              href: pagy.prev ? `/admin/adjustments` : null,
              data: { ...filters, page: pagy.prev },
            }}
            next={{
              href: pagy.next ? `/admin/adjustments` : null,
              data: { ...filters, page: pagy.next },
            }}
          />
        </div>
      </div>
    </AdminLayout>
  );
}
